/**
 * Modern Instagram-Style Chat Input Component
 * 
 * Features:
 * - Instagram-style @mention autocomplete
 * - Modern UI with floating labels
 * - Private note mode with amber styling
 * - Keyboard shortcuts and navigation
 * - Smooth animations and transitions
 * - Real-time agent suggestions
 */

import React, { useState, useRef, useCallback, useEffect } from 'react'
import { cn } from '../../utils/cn'
import { logger } from '../../utils/logger'
import { 
  Send, 
  Lock, 
  Smile, 
  Paperclip, 
  Mic,
  Hash,
  AtSign
} from 'lucide-react'
import { MentionAutocomplete } from './MentionAutocomplete'
import { useMentionAutocomplete, type Agent } from '../../hooks/useMentionAutocomplete'

export interface ModernChatInputProps {
  /** Current message text */
  value: string
  /** Callback when text changes */
  onChange: (value: string) => void
  /** Callback when message is sent */
  onSend: (message: string) => void
  /** Private note mode */
  isNoteMode?: boolean
  /** Toggle note mode */
  onToggleNoteMode?: () => void
  /** Conversation ID for @mentions */
  conversationId: string
  /** Placeholder text */
  placeholder?: string
  /** Disabled state */
  disabled?: boolean
  /** Show recording button */
  showRecording?: boolean
  /** Show attachments button */
  showAttachments?: boolean
  /** Show emoji picker */
  showEmoji?: boolean
  /** Custom CSS classes */
  className?: string
}

function ModernChatInputComponent({
  value,
  onChange,
  onSend,
  isNoteMode = false,
  onToggleNoteMode,
  conversationId,
  placeholder,
  disabled = false,
  showRecording = true,
  showAttachments = true,
  showEmoji = true,
  className
}: ModernChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [isTyping, setIsTyping] = useState(false)

  // @mention functionality
  const {
    agents,
    isLoading: isLoadingAgents,
    mentionState,
    handleTextareaInput,
    handleKeyDown,
    handleAgentSelect,
    closeMentionDropdown
  } = useMentionAutocomplete({
    conversationId,
    textareaRef,
    onMentionSelect: (agent: Agent, mentionText: string) => {
      logger.info('✅ Agent mentioned', {
        agentId: agent.id,
        username: agent.username,
        mentionText
      })
      // Update the input value with the complete text including the @mention
      onChange(mentionText)
    }
  })

  // Auto-resize textarea
  const adjustTextareaHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      const scrollHeight = textareaRef.current.scrollHeight
      const maxHeight = 120 // Max 5 lines
      textareaRef.current.style.height = Math.min(scrollHeight, maxHeight) + 'px'
    }
  }, [])

  // Handle input changes
  const handleInput = useCallback((e: React.FormEvent<HTMLTextAreaElement>) => {
    const newValue = e.currentTarget.value
    onChange(newValue)
    
    // Auto-resize
    adjustTextareaHeight()
    
    // Typing indicator
    setIsTyping(newValue.length > 0)
    
    // Handle @mention detection
    handleTextareaInput(e)
  }, [onChange, adjustTextareaHeight, handleTextareaInput])

  // Handle key presses
  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Handle mention autocomplete navigation
    handleKeyDown(e)
    
    // Send message on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey && !mentionState.isVisible) {
      e.preventDefault()
      if (value.trim()) {
        onSend(value.trim())
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto'
        }
      }
    }
  }, [value, onSend, handleKeyDown, mentionState.isVisible])

  // Handle send button click
  const handleSendClick = useCallback(() => {
    if (value.trim() && !disabled) {
      onSend(value.trim())
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }, [value, onSend, disabled])

  // Calculate mention dropdown position relative to container
  const getMentionPosition = useCallback(() => {
    if (!textareaRef.current || !containerRef.current) {
      return { top: 0, left: 0 }
    }

    // Position dropdown above the input, Instagram style
    return {
      top: -220, // Well above the input (dropdown height ~200px)
      left: 20   // Slight indent from left edge
    }
  }, [])

  // Update mention position when state changes
  useEffect(() => {
    if (mentionState.isVisible) {
      const position = getMentionPosition()
      logger.info('🎯 Updating mention position', { 
        originalPosition: mentionState.position,
        newPosition: position 
      })
    }
  }, [mentionState.isVisible, getMentionPosition])

  // Auto-focus only on initial mount (prevent focus disruption on conversation change)
  useEffect(() => {
    if (textareaRef.current && !value) {
      textareaRef.current.focus()
    }
  }, [])

  const dynamicPlaceholder = placeholder || (
    isNoteMode 
      ? "Add a private note (try @username to mention agents)..." 
      : "Type your message (try @username to mention agents)..."
  )

  return (
    <div 
      ref={containerRef}
      className={cn(
        "relative w-full",
        className
      )}
    >
      {/* Main Input Container */}
      <div className={cn(
        "relative flex items-end gap-3 p-4 bg-white border-2 rounded-2xl transition-all duration-200 shadow-sm",
        isFocused 
          ? (isNoteMode ? "border-amber-400 shadow-amber-100" : "border-blue-500 shadow-blue-100")
          : (isNoteMode ? "border-amber-300" : "border-gray-300"),
        "hover:border-opacity-80",
        disabled && "opacity-50 cursor-not-allowed"
      )}>
        
        {/* Left Action Buttons */}
        <div className="flex items-center gap-2">
          {/* Note Mode Toggle */}
          {onToggleNoteMode && (
            <button
              onClick={onToggleNoteMode}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                isNoteMode
                  ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              )}
              disabled={disabled}
            >
              <Lock className="w-4 h-4" />
              <span className="hidden sm:inline">
                {isNoteMode ? "Note" : "Public"}
              </span>
            </button>
          )}

          {/* Attachments */}
          {showAttachments && (
            <button
              onClick={() => {
                // TODO: Implement file attachment
                console.log('📎 File attachment clicked - feature to be implemented')
              }}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              disabled={disabled}
              title="Attach file (coming soon)"
            >
              <Paperclip className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Text Input Area */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleInput}
            onKeyDown={handleKeyPress}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={dynamicPlaceholder}
            disabled={disabled}
            rows={1}
            className={cn(
              "w-full resize-none border-0 outline-none bg-transparent",
              "text-gray-900 placeholder-gray-500",
              "text-base leading-6",
              "min-h-[24px] max-h-[120px]",
              disabled && "cursor-not-allowed"
            )}
            style={{
              minHeight: '24px',
              maxHeight: '120px'
            }}
          />

          {/* Floating Label */}
          {(isFocused || value) && (
            <div className={cn(
              "absolute -top-6 left-0 text-xs font-medium transition-all duration-200",
              isNoteMode ? "text-amber-600" : "text-blue-600"
            )}>
              {isNoteMode ? "Private Note" : "Message"}
            </div>
          )}

          {/* @Mention Autocomplete Dropdown */}
          <MentionAutocomplete
            isVisible={mentionState.isVisible}
            agents={agents}
            searchQuery={mentionState.searchQuery}
            position={getMentionPosition()}
            onSelect={handleAgentSelect}
            onClose={closeMentionDropdown}
            highlightedIndex={mentionState.highlightedIndex}
            isLoading={isLoadingAgents}
          />
        </div>

        {/* Right Action Buttons */}
        <div className="flex items-center gap-2">
          {/* Emoji Picker */}
          {showEmoji && (
            <button
              onClick={() => {
                // TODO: Implement emoji picker
                console.log('😀 Emoji picker clicked - feature to be implemented')
              }}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              disabled={disabled}
              title="Add emoji (coming soon)"
            >
              <Smile className="w-5 h-5" />
            </button>
          )}

          {/* Voice Recording */}
          {showRecording && (
            <button
              onClick={() => {
                // TODO: Implement voice recording
                console.log('🎤 Voice recording clicked - feature to be implemented')
              }}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              disabled={disabled}
              title="Voice message (coming soon)"
            >
              <Mic className="w-5 h-5" />
            </button>
          )}

          {/* Send Button */}
          <button
            onClick={handleSendClick}
            disabled={disabled || !value.trim()}
            className={cn(
              "flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-200",
              "focus:outline-none focus:ring-2 focus:ring-offset-2",
              value.trim() && !disabled
                ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 focus:ring-blue-500"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            )}
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Status Indicators */}
      <div className="flex items-center justify-between mt-2 px-2 pb-4">
        {/* Left Status */}
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {isNoteMode && (
            <div className="flex items-center gap-1 text-amber-600">
              <Lock className="w-3 h-3" />
              <span>Private note - only visible to agents</span>
            </div>
          )}
          {mentionState.isVisible && (
            <div className="flex items-center gap-1 text-blue-600">
              <AtSign className="w-3 h-3" />
              <span>Use ↑↓ to navigate, Enter to select</span>
            </div>
          )}
        </div>

        {/* Right Status */}
        <div className="flex items-center gap-2 text-xs text-gray-400">
          {isTyping && (
            <span>Press Enter to send, Shift+Enter for new line</span>
          )}
        </div>
      </div>
    </div>
  )
}
// Export memoized component to prevent unnecessary re-renders
export const ModernChatInput = React.memo(ModernChatInputComponent)
